import type { ReactNode } from 'react'
import type { StyleProp, TextStyle, ViewStyle } from 'react-native'
import { Pressable, Text, View } from 'react-native'

import { colors } from '../../theme'
import { label1 } from '../../theme/typography'
import { Spinner } from './Spinner'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

type ButtonVariant = 'primary' | 'secondary' | 'neutral' | 'warning' | 'ghost'

type ButtonSize = 'md' | 'xl'

type VariantStyle = {
  backgroundColor: string
  textColor: string
  borderColor?: string
}

const BUTTON_VARIANT_STYLES: Record<ButtonVariant, VariantStyle> = {
  primary: {
    backgroundColor: colors.primary,
    textColor: colors.primaryForeground,
  },
  secondary: {
    backgroundColor: colors.coreSecondary,
    textColor: colors.primaryForeground,
  },
  neutral: {
    backgroundColor: colors.fill,
    textColor: colors.textHeader,
    borderColor: colors.textHeader,
  },
  warning: {
    backgroundColor: colors.primary400,
    textColor: colors.textHeader,
  },
  ghost: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    textColor: colors.textHeader,
  },
}

const BUTTON_SIZE_STYLES: Record<ButtonSize, ViewStyle> = {
  md: {
    height: 56,
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  xl: {
    height: 56,
    borderRadius: 16,
    paddingHorizontal: 16,
  },
}

export type ButtonProps = {
  label: string
  onPress: () => void
  variant?: ButtonVariant
  size?: ButtonSize
  disabled?: boolean
  loading?: boolean
  leadingIcon?: ReactNode
  containerClassName?: string
  containerStyle?: StyleProp<ViewStyle>
  labelStyle?: StyleProp<TextStyle>
  spinnerColor?: string
}

export function Button({
  label,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  leadingIcon,
  containerClassName,
  containerStyle,
  labelStyle,
  spinnerColor,
}: ButtonProps) {
  const variantStyle = BUTTON_VARIANT_STYLES[variant]
  const sizeStyle = BUTTON_SIZE_STYLES[size]
  const isOnlyDisabled = disabled && !loading
  const buttonStyle = isOnlyDisabled
    ? {
        backgroundColor: colors.disabled,
      }
    : {
        backgroundColor: variantStyle.backgroundColor,
        borderColor: variantStyle.borderColor,
        borderWidth: variantStyle.borderColor != null ? 1 : 0,
        opacity: loading ? 0.6 : 1,
      }

  const buttonTextStyle = isOnlyDisabled
    ? { color: colors.disabledText }
    : { color: variantStyle.textColor }

  return (
    <Pressable
      className={`items-center justify-center flex-row ${containerClassName ?? ''}`}
      style={[buttonStyle, sizeStyle, containerStyle, { gap: 8 }]}
      accessibilityRole="button"
      disabled={disabled || loading}
      onPress={onPress}
    >
      {loading ? (
        <Spinner size="small" color={spinnerColor ?? variantStyle.textColor} />
      ) : (
        <>
          {leadingIcon != null ? <View>{leadingIcon}</View> : null}
          <Text
            {...NON_SCALING_TEXT_PROPS}
            style={[label1, buttonTextStyle, labelStyle]}
          >
            {label}
          </Text>
        </>
      )}
    </Pressable>
  )
}
